import React, {CSSProperties, ReactNode, useState} from 'react'

import {statusColors, focusableClass, ensureFocusStyles} from '../styles.js'
import P from '../typography/P.js'
import S2 from '../typography/S2.js'
import {buttonResetStyle} from '../buttons/styles.js'
import ErrorReportModal from './ErrorReportModal.js'
import {alertStyle, statusIcons, StatusVariant} from './styles.js'

ensureFocusStyles()

interface AlertProps {
    /** No variant is success; 'danger' is the old name of 'error'; 'errorHandler' shows err with a button to report it */
    variant?: StatusVariant | 'danger' | 'errorHandler'
    /** A string is split into lines (\n) */
    children?: ReactNode
    /** Shows a close button that calls onClose; the caller hides the Alert */
    onClose?: () => void
    /** Shows a close button that hides the Alert itself (until its message changes), for an Alert without onClose */
    dismissible?: boolean
    /** Instead of the icon of the variant */
    icon?: string
    /** errorHandler: the error shown and reported */
    err?: Error
    /** errorHandler: sent along with the report */
    customMessage?: string
    style?: CSSProperties
}

/**
 * A message on the pale colour of its status, with the status icon. Errors and warnings are announced right away
 * (role alert), success and info politely (role status). No outer margins.
 */
export default function Alert({variant, children, onClose, dismissible, icon, err, customMessage, style = {}}: AlertProps) {
    // errorHandler: the report form is open
    const [reporting, setReporting] = useState(false)
    // The content that was dismissed: a new message (string) shows the Alert again
    const [dismissedContent, setDismissedContent] = useState<{content: ReactNode}>(null)

    const status: StatusVariant = variant === 'danger' || variant === 'errorHandler' ? 'error' : (statusColors[variant] ? variant : 'success')

    if (dismissedContent && (dismissedContent.content === children || typeof children !== 'string')) {
        return null
    }

    const alertBoxStyle: CSSProperties = {
        position: 'relative',
        display: 'flex',
        flexShrink: 0,
        width: '100%',
        minHeight: alertStyle.minHeight,
        padding: alertStyle.padding,
        background: statusColors[status].pale,
        borderRadius: alertStyle.radius,
        color: alertStyle.text,
        wordBreak: 'break-word',
        ...style
    }

    const role = status === 'error' || status === 'warning' ? 'alert' : 'status'
    const iconElement = <i aria-hidden style={{marginRight: alertStyle.iconGap, fontSize: alertStyle.iconSize}} className={icon || statusIcons[status]} />

    if (variant === 'errorHandler') {
        return (
            <P role={role} style={alertBoxStyle}>
                <div style={{width: '100%'}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        {iconElement}

                        <div style={{flex: 1}}>
                            <P>Onderstaande fout is opgetreden:</P>
                            <P>{err?.message}</P>
                        </div>
                    </div>

                    <div style={{display: 'flex'}}>
                        <button type='button' className={focusableClass} style={{...buttonResetStyle, marginLeft: 30, marginTop: 15, cursor: 'pointer'}} onClick={() => setReporting(true)}>
                            <S2 style={{fontWeight: 'bold'}}>Meld deze fout</S2>
                        </button>
                        <ErrorReportModal show={reporting} err={err} customMessage={customMessage} reportOnly onClose={() => setReporting(false)} />
                    </div>
                </div>
            </P>
        )
    }

    const closable = !!onClose || dismissible

    const close = () => {
        if (onClose) {
            onClose()
        } else {
            setDismissedContent({content: children})
        }
    }

    return (
        <div role={role} style={alertBoxStyle}>
            {iconElement}

            <div style={{flex: 1, display: style.display}}>
                {typeof children === 'string' ?
                    children.split(/\n/).map((child, index) => <P key={index}>{child}</P>) :
                    children
                }
            </div>

            {closable &&
                <button
                    type='button'
                    aria-label='Sluiten'
                    className={focusableClass}
                    style={{...buttonResetStyle, alignSelf: 'flex-start', cursor: 'pointer', marginLeft: alertStyle.iconGap, fontSize: alertStyle.iconSize}}
                    onClick={close}
                >
                    <i aria-hidden className='mdi mdi-close' />
                </button>
            }
        </div>
    )
}
